import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  SelectChangeEvent,
  MenuItem,
  Checkbox,
  ListItemText,
  Alert,
  Box,
  CircularProgress,
} from '@mui/material';

interface UserEditFormProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (userData: UpdateUserData) => Promise<void>;
  user: User | null;
}

interface MuseumOption {
  id: number;
  name: string;
  museum_code: string;
}

export interface User {
  id: number;
  username: string;
  email: string;
  first_name: string;
  last_name: string;
  phone_number: string | null;
  department: string | null;
  position: string | null;
  notes: string | null;
  role: string;
  role_display: string;
  museum_id: number | null;
  museum_name?: string | null;
  museum_ids?: number[];
  museum_names?: string[];
  is_active: boolean;
  last_password_change: string;
  created_by_username: string | null;
  date_joined: string;
  last_login: string | null;
  updated_at: string;
}

export interface UpdateUserData {
  username: string;
  email: string;
  first_name: string;
  last_name: string;
  phone_number: string;
  department: string;
  position: string;
  notes: string;
  role: string;
  museum_ids: number[];
  museum_id?: number;
  is_active: boolean;
}

const UserEditForm: React.FC<UserEditFormProps> = ({ open, onClose, onSubmit, user }) => {
  const [formData, setFormData] = useState<UpdateUserData>({
    username: '',
    email: '',
    first_name: '',
    last_name: '',
    phone_number: '',
    department: '',
    position: '',
    notes: '',
    role: 'user',
    museum_ids: [],
    is_active: true,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [museums, setMuseums] = useState<MuseumOption[]>([]);
  const [isLoadingMuseums, setIsLoadingMuseums] = useState(false);

  const fetchMuseums = async () => {
    try {
      setIsLoadingMuseums(true);
      const token = localStorage.getItem('token');
      const response = await fetch('/api/museums/', {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to load museums');
      }

      const data: unknown = await response.json();
      const museumsData =
        typeof data === 'object' && data && 'results' in data
          ? (data as { results?: MuseumOption[] }).results || []
          : (data as MuseumOption[]);
      setMuseums(museumsData);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to load museums';
      setError(message);
    } finally {
      setIsLoadingMuseums(false);
    }
  };

  useEffect(() => {
    if (user) {
      setFormData({
        username: user.username,
        email: user.email || '',
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        phone_number: user.phone_number || '',
        department: user.department || '',
        position: user.position || '',
        notes: user.notes || '',
        role: user.role,
        museum_ids: user.museum_ids?.length ? user.museum_ids : user.museum_id ? [user.museum_id] : [],
        is_active: user.is_active,
      });
    }
  }, [user]);

  useEffect(() => {
    if (open) {
      fetchMuseums();
    }
  }, [open]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value,
    }));
    if (error) setError(null);
  };

  const handleRoleChange = (e: SelectChangeEvent<string>) => {
    setFormData(prev => ({
      ...prev,
      role: e.target.value,
    }));
  };

  const handleMuseumChange = (e: SelectChangeEvent<string[]>) => {
    const value = e.target.value;
    setFormData(prev => ({
      ...prev,
      museum_ids: (typeof value === 'string' ? value.split(',') : value).filter(Boolean).map(Number),
    }));
  };

  const validateForm = () => {
    if (!formData.username) {
      setError('Username is required');
      return false;
    }
    if (formData.museum_ids.length === 0) {
      setError('At least one museum selection is required');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsLoading(true);
    setError(null);

    try {
      await onSubmit({
        ...formData,
        museum_id: formData.museum_ids[0],
      });
      onClose();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to update user';
      setError(message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      setError(null);
      onClose();
    }
  };

  if (!user) return null;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit User: {user.username}</DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              required
              fullWidth
              label="Username"
              name="username"
              value={formData.username}
              onChange={handleChange}
              disabled={isLoading}
            />

            <TextField
              fullWidth
              label="Email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              disabled={isLoading}
            />

            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                fullWidth
                label="First Name"
                name="first_name"
                value={formData.first_name}
                onChange={handleChange}
                disabled={isLoading}
              />
              <TextField
                fullWidth
                label="Last Name"
                name="last_name"
                value={formData.last_name}
                onChange={handleChange}
                disabled={isLoading}
              />
            </Box>

            <TextField
              fullWidth
              label="Phone Number"
              name="phone_number"
              value={formData.phone_number}
              onChange={handleChange}
              disabled={isLoading}
            />

            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                fullWidth
                label="Department"
                name="department"
                value={formData.department}
                onChange={handleChange}
                disabled={isLoading}
              />
              <TextField
                fullWidth
                label="Position"
                name="position"
                value={formData.position}
                onChange={handleChange}
                disabled={isLoading}
              />
            </Box>

            <TextField
              fullWidth
              label="Notes"
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              multiline
              rows={3}
              disabled={isLoading}
            />

            <FormControl fullWidth required>
              <InputLabel>Museums</InputLabel>
              <Select
                multiple
                value={formData.museum_ids.map(String)}
                onChange={handleMuseumChange}
                label="Museums"
                disabled={isLoading || isLoadingMuseums}
                renderValue={selected =>
                  (selected as string[])
                    .map(id => museums.find(museum => museum.id === Number(id))?.name)
                    .filter(Boolean)
                    .join(', ')
                }
              >
                {museums.map(museum => (
                  <MenuItem key={museum.id} value={String(museum.id)}>
                    <Checkbox checked={formData.museum_ids.includes(museum.id)} />
                    <ListItemText primary={museum.name} secondary={museum.museum_code} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Role</InputLabel>
              <Select value={formData.role} onChange={handleRoleChange} label="Role" disabled={isLoading}>
                <MenuItem value="admin">Admin</MenuItem>
                <MenuItem value="content_user">Content User</MenuItem>
                <MenuItem value="user">User</MenuItem>
                <MenuItem value="tech_support">Tech Support</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={isLoading || !formData.username || formData.museum_ids.length === 0}
          >
            {isLoading ? <CircularProgress size={24} /> : 'Update User'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default UserEditForm;
